import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Eye, Check, X, Clock, FileText, ExternalLink, Download, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { getFileUrl, getImageUrl } from '../../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const RegistrationsManagement = () => {
  const { token } = useAuth();
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedReg, setSelectedReg] = useState(null);

  useEffect(() => {
    fetchRegistrations();
  }, [filter]);

  const fetchRegistrations = async () => {
    try {
      const url = filter === 'all'
        ? `${API_URL}/api/registrations/`
        : `${API_URL}/api/registrations/?status=${filter}`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRegistrations(response.data);
    } catch (error) {
      toast.error('فشل تحميل الطلبات');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status, notes = '') => {
    try {
      await axios.put(
        `${API_URL}/api/registrations/${id}`,
        { status, notes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('تم تحديث حالة الطلب');
      fetchRegistrations();
      setSelectedReg(null);
    } catch (error) {
      toast.error('فشل تحديث الحالة');
    }
  };

  const StatusBadge = ({ status }) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-700',
      reviewed: 'bg-blue-100 text-blue-700',
      accepted: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700'
    };
    
    const labels = {
      pending: 'قيد المراجعة',
      reviewed: 'تمت المراجعة',
      accepted: 'مقبول',
      rejected: 'مرفوض'
    };

    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${styles[status]}`}>
        {labels[status]}
      </span>
    );
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">
      <div className="w-12 h-12 border-4 border-[#0F388E] border-t-transparent rounded-full animate-spin"></div>
    </div>;
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-[#0F388E] mb-2">إدارة طلبات التسجيل</h1>
        <p className="text-gray-600">عرض ومراجعة طلبات الطلاب</p>
      </div>

      <div className="flex gap-3 mb-6">
        {['all', 'pending', 'reviewed', 'accepted', 'rejected'].map((status) => (
          <Button
            key={status}
            onClick={() => setFilter(status)}
            className={filter === status ? 'bg-[#0F388E]' : 'bg-gray-500'}
          >
            {status === 'all' && 'الكل'}
            {status === 'pending' && 'قيد المراجعة'}
            {status === 'reviewed' && 'تمت المراجعة'}
            {status === 'accepted' && 'مقبول'}
            {status === 'rejected' && 'مرفوض'}
          </Button>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-4">
        {registrations.map((reg) => (
          <Card key={reg.id} className="hover:shadow-lg transition-shadow">
            <CardContent className="p-6">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <h3 className="text-xl font-bold text-[#0F388E]">{reg.full_name}</h3>
                    <StatusBadge status={reg.status} />
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">البريد:</span>
                      <p className="font-medium" dir="ltr">{reg.email}</p>
                    </div>
                    <div>
                      <span className="text-gray-600">الهاتف:</span>
                      <p className="font-medium" dir="ltr">{reg.phone}</p>
                    </div>
                    <div>
                      <span className="text-gray-600">البرنامج:</span>
                      <p className="font-medium">{reg.desired_program}</p>
                    </div>
                    <div>
                      <span className="text-gray-600">الكلية:</span>
                      <p className="font-medium">{reg.desired_college}</p>
                    </div>
                    <div>
                      <span className="text-gray-600">المؤهل:</span>
                      <p className="font-medium">{reg.qualification}</p>
                    </div>
                    <div>
                      <span className="text-gray-600">سنة التخرج:</span>
                      <p className="font-medium">{reg.graduation_year}</p>
                    </div>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button
                    onClick={() => setSelectedReg(reg)}
                    className="bg-blue-500 hover:bg-blue-600"
                    size="sm"
                  >
                    <Eye className="w-4 h-4 ml-1" />
                    عرض
                  </Button>
                  {reg.status === 'pending' && (
                    <>
                      <Button
                        onClick={() => updateStatus(reg.id, 'accepted')}
                        className="bg-green-500 hover:bg-green-600"
                        size="sm"
                      >
                        <Check className="w-4 h-4" />
                      </Button>
                      <Button
                        onClick={() => updateStatus(reg.id, 'rejected')}
                        className="bg-red-500 hover:bg-red-600"
                        size="sm"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {selectedReg && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={() => setSelectedReg(null)}>
          <Card className="max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <CardContent className="p-6">
              <h2 className="text-2xl font-bold text-[#0F388E] mb-6">تفاصيل الطلب</h2>
              
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-600">الاسم الكامل</label>
                    <p className="font-medium">{selectedReg.full_name}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">الحالة</label>
                    <div className="mt-1"><StatusBadge status={selectedReg.status} /></div>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">البريد الإلكتروني</label>
                    <p className="font-medium" dir="ltr">{selectedReg.email}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">رقم الهاتف</label>
                    <p className="font-medium" dir="ltr">{selectedReg.phone}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">الجنس</label>
                    <p className="font-medium">{selectedReg.gender}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">تاريخ الميلاد</label>
                    <p className="font-medium">{selectedReg.birth_date}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">الجنسية</label>
                    <p className="font-medium">{selectedReg.nationality}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">المدينة</label>
                    <p className="font-medium">{selectedReg.city}</p>
                  </div>
                </div>

                <div>
                  <label className="text-sm text-gray-600">العنوان</label>
                  <p className="font-medium">{selectedReg.address}</p>
                </div>

                <hr />
                <h3 className="font-bold text-lg">المعلومات التعليمية</h3>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-600">المؤهل</label>
                    <p className="font-medium">{selectedReg.qualification}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">سنة التخرج</label>
                    <p className="font-medium">{selectedReg.graduation_year}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">المعدل</label>
                    <p className="font-medium">{selectedReg.gpa || '-'}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">اسم المدرسة</label>
                    <p className="font-medium">{selectedReg.school_name}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">البرنامج المطلوب</label>
                    <p className="font-medium">{selectedReg.desired_program}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">الكلية المطلوبة</label>
                    <p className="font-medium">{selectedReg.desired_college}</p>
                  </div>
                </div>

                {/* الوثائق والمستندات المرفقة */}
                <hr />
                <h3 className="font-bold text-lg text-[#0F388E]">الوثائق والمستندات المرفقة</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    { label: 'الصورة الشخصية', url: selectedReg.personal_photo || selectedReg.documents?.personal_photo },
                    { label: 'استمارة / شهادة الثانوية', url: selectedReg.high_school_cert || selectedReg.documents?.high_school_cert },
                    { label: 'بطاقة رقم الجلوس', url: selectedReg.seating_number_card || selectedReg.documents?.seating_number_card },
                    { label: 'البطاقة الشخصية / إثبات الهوية', url: selectedReg.national_id_card || selectedReg.documents?.national_id_card },
                  ].map((docItem, idx) => {
                    const rawDocUrl = typeof docItem.url === 'object' ? docItem.url?.url : docItem.url;
                    const fullUrl = rawDocUrl ? getFileUrl(rawDocUrl) : null;
                    const isImg = fullUrl && /\.(jpg|jpeg|png|webp|gif)$/i.test(fullUrl);

                    return (
                      <div key={idx} className="p-3.5 rounded-xl border border-gray-200 bg-gray-50 flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                          {fullUrl && isImg ? (
                            <img 
                              src={fullUrl} 
                              alt={docItem.label} 
                              className="w-12 h-12 rounded-lg object-cover border border-gray-300 flex-shrink-0"
                            />
                          ) : (
                            <div className="w-12 h-12 rounded-lg bg-white border border-gray-200 flex items-center justify-center flex-shrink-0">
                              <FileText className={`w-6 h-6 ${fullUrl ? 'text-[#0F388E]' : 'text-gray-300'}`} />
                            </div>
                          )}
                          <div className="min-w-0">
                            <p className="text-sm font-bold text-gray-800 truncate">{docItem.label}</p>
                            <p className="text-xs text-gray-500">
                              {fullUrl ? 'مرفق ومتاح للمعاينة' : 'لم يتم إرفاق هذا المستند'}
                            </p>
                          </div>
                        </div>

                        {fullUrl ? (
                          <a
                            href={fullUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#0F388E] hover:bg-[#123985] text-white rounded-lg text-xs font-semibold shadow-sm transition-colors flex-shrink-0"
                          >
                            <Eye className="w-3.5 h-3.5" />
                            <span>عرض</span>
                          </a>
                        ) : (
                          <span className="text-xs text-gray-400 bg-gray-200/60 px-2 py-1 rounded">
                            غير متوفر
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>

                {selectedReg.notes && (
                  <div>
                    <label className="text-sm text-gray-600">ملاحظات</label>
                    <p className="font-medium bg-gray-50 p-3 rounded">{selectedReg.notes}</p>
                  </div>
                )}

                <div className="flex gap-3 pt-4">
                  {selectedReg.status === 'pending' && (
                    <>
                      <Button onClick={() => updateStatus(selectedReg.id, 'accepted')} className="bg-green-500">
                        <Check className="w-4 h-4 ml-1" /> قبول
                      </Button>
                      <Button onClick={() => updateStatus(selectedReg.id, 'rejected')} className="bg-red-500">
                        <X className="w-4 h-4 ml-1" /> رفض
                      </Button>
                      <Button onClick={() => updateStatus(selectedReg.id, 'reviewed')} className="bg-blue-500">
                        <Clock className="w-4 h-4 ml-1" /> تمت المراجعة
                      </Button>
                    </>
                  )}
                  <Button onClick={() => setSelectedReg(null)} className="bg-gray-500">
                    إغلاق
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default RegistrationsManagement;